import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { companyInfo } from "@/mocks/help-center";

type FormData = {
  name: string;
  email: string;
  chargeDate: string;
  amount: string;
  currency: string;
  cardFirst6: string;
  cardLast4: string;
  details: string;
};

const EMPTY_FORM: FormData = {
  name: "",
  email: "",
  chargeDate: "",
  amount: "",
  currency: "",
  cardFirst6: "",
  cardLast4: "",
  details: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TODAY = new Date().toISOString().split("T")[0];

function validate(f: FormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!f.name.trim()) {
    errors.name = "Name is required";
  } else if (f.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters";
  }

  if (!f.email.trim()) {
    errors.email = "Email is required";
  } else if (!EMAIL_RE.test(f.email)) {
    errors.email = "Please enter a valid email address";
  }

  if (!f.chargeDate) {
    errors.chargeDate = "Date is required";
  } else if (f.chargeDate > TODAY) {
    errors.chargeDate = "Transaction date cannot be in the future";
  }

  if (!f.amount) {
    errors.amount = "Amount is required";
  } else if (!/^\d+(\.\d{1,2})?$/.test(f.amount) || Number(f.amount) <= 0) {
    errors.amount = "Enter a valid positive amount (e.g. 49.99)";
  }

  if (!f.currency) {
    errors.currency = "Please select a currency";
  }

  if (!f.cardFirst6) {
    errors.cardFirst6 = "Required";
  } else if (f.cardFirst6.length !== 6) {
    errors.cardFirst6 = "Must be exactly 6 digits";
  }

  if (!f.cardLast4) {
    errors.cardLast4 = "Required";
  } else if (f.cardLast4.length !== 4) {
    errors.cardLast4 = "Must be exactly 4 digits";
  }

  if (!f.details.trim()) {
    errors.details = "Description is required";
  } else if (f.details.trim().length < 20) {
    errors.details = "Minimum 20 characters required";
  }

  return errors;
}

/** Strip all non-digit characters, clamp to maxLen */
function digitsOnly(value: string, maxLen: number): string {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

/** Allow only valid decimal number format: digits with at most one dot and up to 2 decimal places */
function sanitizeAmount(value: string): string {
  const stripped = value.replace(/[^\d.]/g, "");
  const parts = stripped.split(".");
  if (parts.length === 1) return parts[0];
  return parts[0] + "." + parts[1].slice(0, 2);
}

function fieldCls(error?: string): string {
  return [
    "w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors bg-white",
    error
      ? "border-red-400 focus:border-red-400"
      : "border-gray-200 focus:border-primary",
  ].join(" ");
}

export default function DisputePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);

  function set(field: keyof FormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    const text = [
      `Name: ${formData.name}`,
      `Card: ${formData.cardFirst6}xxxxxx${formData.cardLast4}`,
      `Transaction date: ${formData.chargeDate}`,
      `Amount: ${formData.amount} ${formData.currency}`,
      ``,
      formData.details,
    ].join("\n");

    const payload = {
      email: formData.email,
      text,
      problemType: "Dispute Resolution",
      to_email: "support@vantorylabs.com",
      subject: "Dispute Resolution Form",
    };

    try {
      await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch {
      setServerError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-ice">
      <header className="bg-white/70 backdrop-blur-md border-b border-cream-dark/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-5">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-primary hover:opacity-70 transition-opacity cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ice">
              <i className="ri-arrow-left-line text-xl text-primary" />
            </div>
            <span className="font-semibold text-sm md:text-base">
              Back to Help Center
            </span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-3 tracking-tight font-heading">
            DISPUTE RESOLUTION
          </h1>
          <div className="w-12 h-1 bg-accent mb-4 rounded-full" />
          <p className="text-base md:text-lg text-gray-500 max-w-2xl">
            Submit a formal dispute resolution request. We take all disputes
            seriously and aim to resolve them within 5-7 business days.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,64,143,0.06)] border border-cream-dark/40 p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-2xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-primary mb-2 font-heading">
              Dispute Submitted
            </h2>
            <p className="text-gray-500 mb-6">
              Your dispute resolution request has been received. Case reference:{" "}
              DIS-{Date.now().toString().slice(-8)}
            </p>
            <p className="text-gray-500 mb-6">
              Our team will review your case and respond within 5-7 business days.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData(EMPTY_FORM);
                setErrors({});
              }}
              className="bg-primary text-white px-6 py-3 font-semibold text-sm hover:bg-primary/90 transition-colors rounded-full whitespace-nowrap cursor-pointer"
            >
              Submit Another Dispute
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,64,143,0.06)] border border-cream-dark/40 p-6 md:p-10"
          >
            <div className="space-y-8">

              {/* Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-primary mb-3">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    className={fieldCls(errors.name)}
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-primary mb-3">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    className={fieldCls(errors.email)}
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Date + Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-primary mb-3">
                    Date of Transaction
                  </label>
                  <input
                    type="date"
                    name="chargeDate"
                    max={TODAY}
                    className={fieldCls(errors.chargeDate)}
                    value={formData.chargeDate}
                    onChange={(e) => set("chargeDate", e.target.value)}
                  />
                  {errors.chargeDate && (
                    <p className="text-xs text-red-500 mt-1">{errors.chargeDate}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-primary mb-3">
                    Amount
                  </label>
                  <input
                    type="text"
                    name="amount"
                    inputMode="decimal"
                    className={fieldCls(errors.amount)}
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => set("amount", sanitizeAmount(e.target.value))}
                  />
                  {errors.amount && (
                    <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
                  )}
                </div>
              </div>

              {/* Currency + Card digits */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
                <div className="sm:col-span-5">
                  <label className="block text-sm font-bold text-primary mb-3">
                    Currency
                  </label>
                  <select
                    name="currency"
                    className={fieldCls(errors.currency) + " cursor-pointer"}
                    value={formData.currency}
                    onChange={(e) => set("currency", e.target.value)}
                  >
                    <option value="">Select currency</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="AUD">AUD</option>
                    <option value="CAD">CAD</option>
                  </select>
                  {errors.currency && (
                    <p className="text-xs text-red-500 mt-1">{errors.currency}</p>
                  )}
                </div>
                <div className="sm:col-span-4">
                  <label className="block text-sm font-bold text-primary mb-3">
                    First 6 digits
                  </label>
                  <input
                    type="text"
                    name="cardFirst6"
                    inputMode="numeric"
                    maxLength={6}
                    className={fieldCls(errors.cardFirst6)}
                    placeholder="123456"
                    value={formData.cardFirst6}
                    onChange={(e) => set("cardFirst6", digitsOnly(e.target.value, 6))}
                  />
                  {errors.cardFirst6 && (
                    <p className="text-xs text-red-500 mt-1">{errors.cardFirst6}</p>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm font-bold text-primary mb-3">
                    Last 4 digits
                  </label>
                  <input
                    type="text"
                    name="cardLast4"
                    inputMode="numeric"
                    maxLength={4}
                    className={fieldCls(errors.cardLast4)}
                    placeholder="1234"
                    value={formData.cardLast4}
                    onChange={(e) => set("cardLast4", digitsOnly(e.target.value, 4))}
                  />
                  {errors.cardLast4 && (
                    <p className="text-xs text-red-500 mt-1">{errors.cardLast4}</p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div>
                <label className="block text-sm font-bold text-primary mb-3">
                  Issue Description
                </label>
                <textarea
                  name="details"
                  rows={8}
                  maxLength={500}
                  className={fieldCls(errors.details) + " resize-none"}
                  placeholder="Please provide detailed information about your dispute. Include what happened, when it occurred, and what resolution you are seeking."
                  value={formData.details}
                  onChange={(e) => set("details", e.target.value)}
                />
                <div className="flex items-center justify-between mt-2">
                  {errors.details ? (
                    <p className="text-xs text-red-500">{errors.details}</p>
                  ) : (
                    <span className="text-xs text-gray-400">
                      Minimum 20 characters required
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">
                    {formData.details.length} / 500
                  </span>
                </div>
              </div>

              {serverError && (
                <p className="text-sm text-red-500">
                  Something went wrong. Please try again later.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-primary text-white px-8 py-4 font-semibold text-sm hover:bg-primary/90 transition-colors rounded-full whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        )}
      </main>

      <footer className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
          <p className="text-sm text-white/60">{companyInfo.name} — Help Center</p>
        </div>
      </footer>
    </div>
  );
}
